import { useEffect, useMemo, useState } from "react";
import { AlertCircle, BellRing, ClipboardList, ExternalLink, LoaderCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { getKycCaseDetail } from "../lib/api";
import { canEscalate, suggestedEscalationKind } from "../lib/kycEscalation";
import { kycAgeDays, kycStage } from "../lib/kycOps";
import { useTimeZone } from "../lib/TimeZoneContext";
import { formatTimestamp } from "../lib/timeZone";
import { formatPersonName } from "../lib/directory";
import { CopyValue } from "./CopyValue";
import { KycEscalationPanel } from "./KycEscalationPanel";
import { StageGuidance } from "./StageGuidance";
import type { AdminUserSummary, KycCase, KycCaseDetail, Viewer } from "../types";

interface PanelProps {
  user: AdminUserSummary | null;
  item?: KycCase;
  viewer: Viewer;
  onManage: () => void;
  onNotify: () => void;
  onRecheck: () => void;
  onOpenCustomer: () => void;
  onEscalate: () => void;
  onUpdateEscalation: () => void;
  /** Bumped by the page after a write so the timeline refetches without a full reload. */
  refreshToken?: number;
}

export function KycInvestigationPanel({ user, item, viewer, onManage, onNotify, onRecheck, onOpenCustomer, onEscalate, onUpdateEscalation, refreshToken = 0 }: PanelProps) {
  const { timeZone } = useTimeZone();
  const [detail, setDetail] = useState<KycCaseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stage = kycStage(user?.state ?? "unknown");
  const canViewReferences = true;
  const canAct = viewer.role === "superAdmin";

  useEffect(() => {
    if (!user?.customerGuid) { setDetail(null); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    void getKycCaseDetail(user.customerGuid)
      .then((result) => !cancelled && setDetail(result))
      .catch((reason) => !cancelled && setError(reason instanceof Error ? reason.message : "Unable to load case timeline"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [user?.customerGuid, refreshToken]);

  /**
   * The customer list only carries one merged state string. The raw provider pair that
   * decides everything — `kyc_state` versus `identity_verification_state` — is only ever
   * returned by a recheck, so the last one is dug out of the audit trail and shown
   * beside the derived status. Staff otherwise cannot see the pair at all, which is the
   * documented cause of "completed means approved" misreads.
   */
  const lastRecheck = useMemo(() => (detail?.events ?? [])
    .find((event) => event.action === "kyc.status.recheck" && (event.providerState || event.verificationState)), [detail]);

  const timeline = useMemo(() => [
    ...(detail?.notes ?? []).map((note) => ({ id: `note-${note.id}`, at: note.createdAt, kind: "Internal note", title: note.text, detail: note.reason, actor: note.actorEmail })),
    ...(detail?.events ?? []).map((event) => {
      const title = event.action === "kyc.status.recheck"
        ? `Provider recheck: ${event.providerState ?? "unknown"} / ${event.verificationState ?? "unknown"}`
        : event.action === "kyc.case.update"
          ? `Case updated to ${(event.workflowStatus ?? "unknown").replaceAll("_", " ")}`
          : event.action.replaceAll(".", " ");
      const detailText = event.action === "kyc.case.update"
        ? `${event.priority ?? "normal"} priority · ${event.assigneeEmail || "unassigned"}`
        : event.appStatus ? `App status: ${event.appStatus}` : event.reason;
      return { id: `event-${event.id}`, at: event.occurredAt, kind: event.state, title, detail: detailText, actor: event.actorEmail };
    }),
    ...(user?.identityCreatedAt ? [{ id: "identity-created", at: user.identityCreatedAt, kind: "Provider", title: "Identity verification created", detail: user.identityVerificationGuid, actor: null }] : [])
  ].sort((left, right) => (right.at ?? "").localeCompare(left.at ?? "")), [detail, user]);

  if (!user) return <div className="detail-empty"><ShieldCheck size={28} /><strong>Select a case</strong><span>Guidance, identifiers, and history appear here.</span></div>;

  return <div className="detail-panel">
    <p className="section-label">CASE DETAIL</p>
    <h2>{formatPersonName(user.name) ?? "Unnamed user"}</h2>
    <p className="detail-panel-sub">{user.emailMasked || "No email"}</p>

    <StageGuidance state={user.state} />

    <section className="detail-panel-section">
      <div className="detail-panel-heading"><h3>Provider signals</h3></div>
      {/* Static rather than behind an info button: misreading this pair is the
          documented cause of wrong KYC calls, so it stays on screen while staff read
          the values it explains. */}
      <p className="detail-panel-note">Approval is <code>kyc_state == verified</code>, never <code>identity_verification_state == completed</code> — the latter only means the check finished, pass or fail. Failure codes are transient system errors, not rejections.</p>
      <dl className="detail-list">
        <div><dt>Directory state</dt><dd>{user.state || "unknown"}</dd></div>
        <div><dt>Last recheck · kyc_state</dt><dd>{lastRecheck?.providerState ?? "Not rechecked from this portal"}</dd></div>
        <div><dt>Last recheck · identity_verification_state</dt><dd>{lastRecheck?.verificationState ?? "—"}</dd></div>
        {/* A completed verification says only that the check finished. The outcome is
            what separates a pass from a retryable failure, and the pinned API contract
            does not return it yet — so an absent value is labelled as unavailable
            rather than left to read as a pass. */}
        <div><dt>Last recheck · outcome</dt><dd>{lastRecheck?.verificationOutcome ?? (lastRecheck ? "Not returned by the API contract" : "—")}</dd></div>
        <div><dt>App status after recheck</dt><dd>{lastRecheck?.appStatus ?? "—"}{lastRecheck?.occurredAt ? <span className="cell-subtitle">{formatTimestamp(lastRecheck.occurredAt, timeZone)}</span> : null}</dd></div>
      </dl>
    </section>

    <section className="detail-panel-section">
      <div className="detail-panel-heading"><h3>Identifiers</h3></div>
      <dl className="detail-list">
        <div><dt>Customer GUID</dt><dd><CopyValue value={user.customerGuid} label="customer GUID" /></dd></div>
        <div><dt>Firebase UID</dt><dd><CopyValue value={user.firebaseUid} label="Firebase UID" /></dd></div>
        <div><dt>Identity verification</dt><dd>{canViewReferences ? <CopyValue value={user.identityVerificationGuid} label="identity verification GUID" /> : "Role restricted"}</dd></div>
        <div><dt>Persona inquiry</dt><dd>{canViewReferences ? <CopyValue value={user.personaInquiryId} label="Persona inquiry ID" /> : "Role restricted"}</dd></div>
        <div><dt>Workflow</dt><dd>{(item?.workflowStatus ?? "unassigned").replaceAll("_", " ")} · {item?.priority ?? "normal"} priority</dd></div>
        <div><dt>Owner</dt><dd>{item?.assigneeEmail || "Unassigned"}</dd></div>
      </dl>
    </section>

    <div className="detail-panel-actions">
      <button className="primary-button full-button" onClick={onOpenCustomer} disabled={!user.customerGuid}><ExternalLink size={16} />Open full profile</button>
      {/* Outreach is the only lever that moves a customer-action case, so it leads there. */}
      {stage === "customerAction" || stage === "blocked"
        ? <button className="secondary-button full-button" onClick={onNotify} disabled={!canAct || !user.firebaseUid}><BellRing size={16} />Send verification notice</button>
        : null}
      <button className="secondary-button full-button" onClick={onRecheck} disabled={!canAct || !user.customerGuid || !user.firebaseUid || !user.identityVerificationGuid}><RefreshCw size={16} />Recheck provider state</button>
      <button className="secondary-button full-button" onClick={onManage} disabled={!canAct || !user.customerGuid || !user.firebaseUid}><ClipboardList size={16} />Manage case</button>
      {stage !== "customerAction" && stage !== "blocked"
        ? <button className="secondary-button full-button" onClick={onNotify} disabled={!canAct || !user.firebaseUid}><BellRing size={16} />Send verification notice</button>
        : null}
    </div>

    <KycEscalationPanel
      escalation={item?.escalation ?? null}
      canOpen={canEscalate(user, item)}
      // Nothing about the state calls for a ticket, so the only honest reason to open
      // one is a duplicate the operator has identified themselves.
      onlyDuplicate={suggestedEscalationKind(user.state, kycAgeDays(user, item)) === null}
      canAct={canAct}
      onOpen={onEscalate}
      onUpdate={onUpdateEscalation}
    />

    <section className="detail-panel-section timeline-section">
      <div className="detail-panel-heading"><h3>Case timeline</h3><span className="history-count">{loading ? "Loading…" : `${timeline.length} events`}</span></div>
      {loading ? <div className="timeline-empty"><LoaderCircle className="spin" size={21} />Loading history…</div> : null}
      {error ? <div className="timeline-empty timeline-error"><AlertCircle size={19} />{error}</div> : null}
      {!loading && !error && timeline.length === 0 ? <div className="timeline-empty">No case activity recorded.</div> : null}
      {!loading && !error && timeline.length ? <ol className="case-timeline">{timeline.map((entry) => <li key={entry.id}><span className="timeline-marker" /><div><small>{entry.kind} · {formatTimestamp(entry.at, timeZone)}</small><strong>{entry.title}</strong>{entry.detail ? <p>{entry.detail}</p> : null}{entry.actor ? <em>{entry.actor}</em> : null}</div></li>)}</ol> : null}
    </section>
  </div>;
}
